import { Alert, Modal as ShownModal } from "antd";
import restClient from "api/restClient";
import { Button } from "components/Button/Button";
import { ActionDropdownButtonWithoutParent } from "components/DropdownButton/ActionDropdownButton";
import { StoreProviderForm } from "components/Form/StoreProviderForm";
import { CancelButton } from "components/Form/components/CancelButton/CancelButton";
import { IconLike } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import IconButton from "components/IconButton";
import {
    getUncommittedStatusBadgeIcon,
    IconWithStatusBadge,
    UncommittedStatusBadgeImage,
} from "components/IconWithStatusBadge/IconWithStatusBadge";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import Tooltip from "components/Tooltip/Tooltip";
import { FeConfigType } from "config/config";
import { getUserData } from "containers/Auth/Auth.selectors";
import { getActiveOrDefaultProjectId } from "containers/Projects/Projects.selectors";
import { Action, ActionData } from "containers/Resources/components/CommonActions/Action";
import { createModalActionSettlement } from "containers/Resources/components/CommonActions/actionModalSettlement";
import {
    forceRecordUpdate,
    getReadOnlyOrDeleted,
} from "containers/Resources/components/CommonActions/CommonActions";
import { BulkCommitAction } from "containers/Resources/screens/ResourcesSingle/components/GitAction/commitActions/BulkCommitAction";
import { BulkResetForm } from "containers/Resources/screens/ResourcesSingle/components/GitAction/commitActions/formComponents/BulkResetForm";
import {
    bulkReset,
    getUncommittedChangesCount,
    isContainerType,
    isGitActionButtonDisabled,
    UncommittedDataGroupedByName,
} from "containers/Resources/screens/ResourcesSingle/components/GitAction/commitActions/utils";
import { ManageGitModal } from "containers/Resources/screens/ResourcesSingle/components/GitAction/ManageGitModal";
import { Bundle, Project, Workspace } from "graphql/generated/graphql";
import { useMemo, useState } from "react";
import { Form as FinalForm } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { getTypedLowerCase } from "utils/service";
import style from "./GitAction.module.scss";

interface GitActionProps {
    data: Project | Bundle | Workspace;
    isDisabled?: boolean;
    afterAction: () => void;
    uncommittedData: UncommittedDataGroupedByName;
    fetchSearchTabs?: () => Promise<any> | undefined;
    isGitLocked?: boolean;
}

interface ModalActionProps {
    data: ActionData;
    afterAction: () => void;
    /** Opens the unified "Manage git" modal (owned by the parent GitAction). */
    onManageGit: () => void;
    /**
     * Closes the enclosing git-actions dropdown. The overlay items are custom
     * <Button>s (not antd Menu.Items), so antd does not auto-close the dropdown
     * on click — each action closes it explicitly before opening its modal.
     */
    closeDropdown: () => void;
}

export const getIsGitEnabled = (data: ActionData) => !!data?.resourceData?.branch_name;
export const getRecordOrContainerIsGitEnabled = (resourceType: FeConfigType, resourceData: any) =>
    isContainerType(resourceType)
        ? !!resourceData?.branch_name
        : resourceData?.bundle_id_bundle ?? resourceData?.project_id_project
        ? !!resourceData?.bundle_id_bundle?.branch_name ||
          !!resourceData?.project_id_project?.branch_name
        : !!resourceData?.workspace_id_workspace?.branch_name;

export const getInitialModal = (title?: string) =>
    ShownModal.confirm({
        title,
        className: style.gitActionModal,
        icon: null,
        onOk: () => {},
        onCancel: () => {},
        okText: "",
        cancelText: "",
        okButtonProps: { disabled: true },
        cancelButtonProps: { disabled: true },
        centered: true,
    });

interface GitWarningContainerProps {
    data: ActionData;
    handleClick: () => void;
    closeModal: () => void;
}

interface GitWarningProps {
    message: string;
    additionalText?: string;
    handleClick: () => void;
    closeModal: () => void;
    buttonLabel: string;
    buttonIcon?: IconLike;
}

const GitWarning = ({
    message,
    additionalText,
    closeModal,
    handleClick,
    buttonLabel,
    buttonIcon,
}: GitWarningProps) => {
    const [isDisabled, setDisabled] = useState(false);

    return (
        <div>
            <div className="warningMessage">
                <Alert message={message} type="warning" />
            </div>
            <div style={{ marginTop: "16px" }}>{additionalText ?? null}</div>
            <div className="modalActions">
                <CancelButton onClick={() => closeModal()} />
                <Button
                    onClick={() => {
                        setDisabled(true);
                        handleClick();
                    }}
                    variation="danger"
                    label={buttonLabel}
                    icon={buttonIcon}
                    loading={isDisabled}
                />
            </div>
        </div>
    );
};

const ResetToStatusInGitContent = ({ data, handleClick, closeModal }: GitWarningContainerProps) => {
    return (
        <GitWarning
            message={`Any changes you made will be lost and the ${data.resourceType} "${data.resourceData.name}" will be reset to the last commit in git.`}
            closeModal={closeModal}
            handleClick={handleClick}
            buttonLabel="Reset to status in git"
            buttonIcon={IconCollection.undo}
        />
    );
};

export const ResetToStatusInGitAction: Action = async (data, dispatch) => {
    const locationIdPath = `${data.resourceType}/${data.resourceData.id}`;

    return new Promise((resolve, reject) => {
        const modal = getInitialModal(
            `Reset to status in git for ${data.resourceType} "${data.resourceData.name}"`
        );

        const settlement = createModalActionSettlement(() => modal.destroy(), { resolve, reject });

        const resetToStatusInGit = () => {
            restClient
                .patch(`${locationIdPath}/git/restore`)
                .then(() => forceRecordUpdate(dispatch))
                .then((result) => settlement.succeed(result))
                .catch((error) => {
                    NotificationService.error(<RestErrorNotification error={error} />);
                    settlement.fail(error);
                });
        };

        modal.update((previousConfig) => ({
            ...previousConfig,
            content: (
                <ResetToStatusInGitContent
                    data={data}
                    handleClick={resetToStatusInGit}
                    closeModal={settlement.cancel}
                />
            ),
        }));
    });
};
export const BulkResetToStatusInGitAction: Action = async (data, dispatch) => {
    const uncommittedData = data?.uncommittedData;
    const uncommittedRecordList = Object.values(uncommittedData ?? {});

    return new Promise((resolve, reject) => {
        const location = data.resourceType;
        const locationName = data.resourceData.name;
        const modal = getInitialModal(
            `Bulk reset to status in git for ${location} "${locationName}"`
        );

        const settlement = createModalActionSettlement(() => modal.destroy(), { resolve, reject });

        const form = (
            <StoreProviderForm>
                <FinalForm
                    onSubmit={(formData: any) =>
                        bulkReset({
                            formData,
                            actionData: data,
                            dispatch,
                        })
                            .then((result) => settlement.succeed(result))
                            .catch((error) => settlement.fail(error))
                    }
                    initialValues={{
                        records: [],
                    }}
                >
                    {(props: any) => (
                        <BulkResetForm
                            uncommittedRecordList={uncommittedRecordList}
                            closeModal={settlement.cancel}
                            {...props}
                        />
                    )}
                </FinalForm>
            </StoreProviderForm>
        );

        modal.update((previousConfig) => ({
            ...previousConfig,
            content: form,
        }));
    });
};

const ManageGitActionList = ({
    data,
    afterAction,
    onManageGit,
    closeDropdown,
}: ModalActionProps) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const uncommittedChangesCount = getUncommittedChangesCount(data?.uncommittedData);

    return (
        <>
            <div className={style.manageGitActions}>
                <Button
                    key={"manageGit"}
                    label={"Manage git"}
                    icon={IconCollection.git}
                    disabled={getReadOnlyOrDeleted(data)}
                    variation="clear"
                    className="wide"
                    onClick={() => {
                        closeDropdown();
                        onManageGit();
                    }}
                />
                <Button
                    key={"bulkCommit"}
                    label={"Bulk commit"}
                    icon={getUncommittedStatusBadgeIcon(uncommittedChangesCount)}
                    image={
                        uncommittedChangesCount ? (
                            <UncommittedStatusBadgeImage
                                uncommittedChangesCount={uncommittedChangesCount}
                            />
                        ) : null
                    }
                    disabled={isGitActionButtonDisabled(data)}
                    variation="clear"
                    className="wide"
                    onClick={() => {
                        closeDropdown();
                        BulkCommitAction({ ...data, afterAction }, dispatch, history);
                    }}
                />
                <Button
                    key={"bulkResetToStatusInGit"}
                    label={"Bulk reset to status in git"}
                    icon={IconCollection.undo}
                    disabled={isGitActionButtonDisabled(data)}
                    variation="clear"
                    className="wide"
                    onClick={() => {
                        closeDropdown();
                        BulkResetToStatusInGitAction({ ...data, afterAction }, dispatch, history);
                    }}
                />
            </div>
        </>
    );
};

export const GitAction = ({
    data,
    isDisabled,
    afterAction,
    uncommittedData,
    fetchSearchTabs,
    isGitLocked,
}: GitActionProps) => {
    const uncommittedChangesCount = getUncommittedChangesCount(uncommittedData);

    const userData = useSelector(getUserData);

    const activeProjectId = useSelector(getActiveOrDefaultProjectId);

    const [manageGitOpen, setManageGitOpen] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const isGitEnabled = useMemo(() => data.branch_name, [data.branch_name]);

    const tooltipOverlay = isGitLocked
        ? "Another git operation in progress"
        : uncommittedChangesCount
        ? `${uncommittedChangesCount} uncommitted change${uncommittedChangesCount > 1 ? "s" : ""}`
        : isGitEnabled
        ? "git enabled"
        : "enable git";

    const actionData = useMemo(
        () => ({
            resourceData: data,
            // bundle/project/workspace rows always carry resource_type (a metadata column)
            resourceType: getTypedLowerCase(
                data.resource_type as NonNullable<typeof data.resource_type>
            ),
            userData,
            activeProjectId,
            isReadOnly: isDisabled ?? false,
            uncommittedData,
        }),
        [activeProjectId, data, isDisabled, uncommittedData, userData]
    );

    const statusBadgeProps = uncommittedChangesCount
        ? { type: "primary-red", text: String(uncommittedChangesCount), size: "smallText" }
        : { type: "primary-green", size: "small", statusBadgeStyle: { top: "-2px" } };

    return (
        <div>
            {isGitEnabled ? (
                <>
                    <ActionDropdownButtonWithoutParent
                        dropdownIcon={IconCollection.git}
                        isDisabled={isDisabled || isGitLocked}
                        key={"gitAction"}
                        trigger={["click"]}
                        className="dropdownActionIcon"
                        tooltipOverlay={tooltipOverlay}
                        visible={dropdownOpen}
                        onVisibleChange={setDropdownOpen}
                        image={
                            <IconWithStatusBadge
                                icon={IconCollection.git}
                                statusBadgeProps={statusBadgeProps}
                            />
                        }
                    >
                        <ManageGitActionList
                            data={actionData}
                            afterAction={afterAction}
                            onManageGit={() => setManageGitOpen(true)}
                            closeDropdown={() => setDropdownOpen(false)}
                        />
                    </ActionDropdownButtonWithoutParent>
                </>
            ) : (
                <Tooltip overlay={tooltipOverlay}>
                    <IconButton
                        onClick={() => setManageGitOpen(true)}
                        icon={IconCollection.git}
                        disabled={isDisabled}
                    />
                </Tooltip>
            )}
            <ManageGitModal
                isOpen={manageGitOpen}
                resourceType={actionData.resourceType}
                resourceData={data}
                userId={userData?.identity_id}
                onClose={() => setManageGitOpen(false)}
                afterAction={() => {
                    afterAction?.();
                    fetchSearchTabs?.();
                }}
            />
        </div>
    );
};
